import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ChevronRight, Building2 } from "lucide-react";
import type { ReferralByLaunch } from "@/hooks/useReferralFunnel";

interface ReferralsByLaunchTableProps {
  data: ReferralByLaunch[];
  onSelectLaunch: (launchId: number) => void;
}

export function ReferralsByLaunchTable({
  data,
  onSelectLaunch,
}: ReferralsByLaunchTableProps) {
  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Por Lanzamiento</CardTitle>
          <CardDescription>
            Métricas de referidos agrupadas por lanzamiento
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4 text-sm text-muted-foreground">
              No hay datos de referidos para mostrar
            </p>
            <p className="text-xs text-muted-foreground">
              Los referidos aparecerán cuando los participantes completen el formulario
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Por Lanzamiento</CardTitle>
        <CardDescription>
          Métricas de referidos agrupadas por lanzamiento. Haz clic en un lanzamiento para ver el detalle.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Lanzamiento</TableHead>
              <TableHead>Cliente</TableHead>
              <TableHead className="text-center">Invitados</TableHead>
              <TableHead className="text-center">Respondidos</TableHead>
              <TableHead className="text-center">Pendientes</TableHead>
              <TableHead className="text-center">Conversión</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((launch) => (
              <TableRow
                key={launch.launchId}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onSelectLaunch(launch.launchId)}
              >
                <TableCell className="font-medium">
                  {launch.launchName}
                </TableCell>
                <TableCell>
                  <span className="text-muted-foreground">{launch.clientName}</span>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="secondary">{launch.invited}</Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-100">
                    {launch.responded}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    {launch.pending}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Progress
                      value={launch.conversionRate}
                      className="h-2 w-16"
                    />
                    <span className="text-sm font-medium">
                      {launch.conversionRate}%
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm">
                    Ver detalle
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
